import { useCallback, useState } from "react";

// Width bounds for the draggable sidebar. Narrower than 240px and the
// titles become unreadable; wider than 480px and the main content
// area gets starved.
const MIN_WIDTH = 240;
const MAX_WIDTH = 480;
const STORAGE_KEY = "hiq.sidebar.width.v1";

export const SIDEBAR_MIN_WIDTH = MIN_WIDTH;
export const SIDEBAR_MAX_WIDTH = MAX_WIDTH;

export function getStoredSidebarWidth(defaultWidth = 280) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultWidth;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return defaultWidth;
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
  } catch {
    return defaultWidth;
  }
}

// Hook consumed by <SidebarLayout> to own the width state + persist
// it across reloads. Kept in a dedicated file so eslint's
// react-refresh/only-export-components rule doesn't flag the mixed
// exports that would happen if we co-located it with the
// <SidebarResizer> component.
export function useSidebarWidth(defaultWidth = 280) {
  const [width, setWidth] = useState(() => getStoredSidebarWidth(defaultWidth));

  const setAndPersist = useCallback((next) => {
    const clamped = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next));
    setWidth(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, String(clamped));
    } catch {
      /* quota / privacy mode — silently skip */
    }
  }, []);

  return [width, setAndPersist];
}
